import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface GifResult {
  id: string;
  url: string;
  preview: string;
  width: number;
  height: number;
}

interface GifPickerProps {
  onSelect: (url: string) => void;
  children: React.ReactNode;
}

export default function GifPicker({ onSelect, children }: GifPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: searchResults } = useQuery<{ results: GifResult[] }>({
    queryKey: [`/api/gifs/search?q=${encodeURIComponent(debouncedQuery)}&limit=20`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: open && debouncedQuery.length > 0,
  });

  const { data: trendingResults } = useQuery<{ results: GifResult[] }>({
    queryKey: ["/api/gifs/trending"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: open && debouncedQuery.length === 0,
  });

  const gifs = debouncedQuery ? searchResults?.results : trendingResults?.results;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {/* z-[70] keeps the popover above ComposeModal's z-60 container */}
      <PopoverContent className="z-[70] w-80 p-0" align="start">
        <div className="p-2 border-b border-(--scroll-border)">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--scroll-text-tertiary)" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search GIFs..."
              className="scroll-input w-full pl-8 text-sm"
              autoFocus
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1 p-1 max-h-[300px] overflow-y-auto">
          {(gifs || []).map((gif) => (
            <button
              key={gif.id}
              onClick={() => {
                onSelect(gif.url);
                setOpen(false);
                setQuery("");
              }}
              className="relative aspect-square overflow-hidden rounded hover:opacity-80 transition-opacity"
            >
              <img
                src={gif.preview}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
          {gifs?.length === 0 && (
            <p className="col-span-2 text-center text-sm text-(--scroll-text-secondary) py-8">
              No GIFs found
            </p>
          )}
        </div>
        <div className="px-2 py-1 border-t border-(--scroll-border)">
          <p className="text-[10px] text-(--scroll-text-tertiary) text-right">Powered by Tenor</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
